import {
  ApplePaymentToken,
  AppleTokenPaymentMethod,
  ConfirmedOrderModel,
  CreditCardModel,
  CreditCardType,
  PayOrderFullGuestBookingInfo,
} from '../../services';
import { createStore, stateSetterHandler } from '@alpha.apps/react-common';

import { storage } from '../../libs';
import { CreditCardCvvModel } from '@/components/checkout/payment-methods';
import { VerifyNumberResponse } from '@/features/stc-pay/types';

export interface OfferRemoveCallbacks {
  successCallback: () => void;
  errorCallback?: () => void;
}

export interface GuestBookingCreditCardModel {
  scheme: CreditCardType;
  last4: string;
  isMada?: boolean;
}

export interface GuestBookingPayInfoModel {
  payToken: string;
  payCardCvv: string;
  payCardBin?: string;
  payCardLast4?: string;
  payExpiryYear?: string;
  payExpiryMonth?: string;
}

interface GuestBookingInfoModel {
  id: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
  isInfoValid?: boolean;
  isGuestVerified?: boolean;
  isMadaCard?: boolean;
  guestUserId?: number;
  isGuestBookingPaymentLoading?: boolean;
  guestBookingCreditCard?: GuestBookingCreditCardModel;
  payInfo?: GuestBookingPayInfoModel;
  isPhoneNumberFocused?: boolean;
  isPhoneNumberVisibleOnly?: boolean;
}

export interface CheckoutOrderModel extends Partial<ConfirmedOrderModel> {
  prevOfferFlowLegalPath?: string;
  isOfferDeleted?: boolean;
  newCardId?: number;
  temporaryAddedCreditCards?: CreditCardModel[];
  selectedCreditCardValidCvv?: string;
  isCreditCardRequired?: boolean;
  isGuestBookingEnabled?: boolean;
  isShowVoucherForm?: boolean;
  isVoucherLoading?: boolean;
  isWalletSelected?: boolean;
  isCashbackWalletSelected?: boolean;
  isApplePaySelected?: boolean;
  isStcPaySelected?: boolean;
  eWalletAmount?: number;
  cashbackWalletAmount?: number;
  handlePayOrder?: (
    cardId: number,
    cardCvv: string,
    payForLimit: boolean,
    applePayPaymentData?: ApplePaymentToken | string,
    applePaymentMethod?: AppleTokenPaymentMethod,
    stcPayInfo?: StcPayDataModel
  ) => void;
  handleGuestBookingPayOrder?: (
    sendVerifiedOfferBin?: boolean,
    payForLimit?: boolean,
    payInfo?: GuestBookingPayInfoModel,
    applePayPaymentData?: string | ApplePaymentToken,
    _guestBookingInfo?: Partial<PayOrderFullGuestBookingInfo>,
    applePaymentMethod?: AppleTokenPaymentMethod,
    stcPayInfo?: StcPayDataModel
  ) => void;
  handleCreditCardsCvvListChange?: (creditCardCvvNewValue: CreditCardCvvModel) => void;
  handleOfferRemove?: (callbacks?: OfferRemoveCallbacks) => void;
  walletRemainSelectedCard?: number;
  walletRemainSelectedCreditCardValidCvv?: string;
  walletRemainCreditCardsCvvList?: CreditCardCvvModel[];
  walletRemainTemporaryAddedCreditCards?: CreditCardModel[];
  handleWalletRemainCreditCardsCvvListChange?: (
    creditCardCvvNewValue: CreditCardCvvModel
  ) => void;
  handleWalletRemainPayOrder?: (
    cardId: number,
    cardCvv: string,
    isApplePay?: boolean,
    applePayPaymentData?: ApplePaymentToken | string,
    applePaymentMethod?: AppleTokenPaymentMethod
  ) => void;
}
export type StcPayDataModel =
  | (Pick<VerifyNumberResponse, 'checkoutPaymentContext'> & {
      stcNumber?: string;
      otp?: string;
      sourceCase?: string;
      isStcNumberValid?: boolean;
    })
  | null;

// TODO: add creditCardsCvvList to order state
// TODO: add selectedCard to order state

export type OrderState = {
  order: CheckoutOrderModel | null;
  guestBookingInfo: GuestBookingInfoModel | null;
  stcPayData: StcPayDataModel | null;
};

const initState: OrderState = {
  order: storage.order.get() ?? null,
  guestBookingInfo: null,
  stcPayData: null,
};

export const useOrder = createStore({
  name: 'order',
  devtools: true,
  persist: false,
})(initState, set => {
  const handler = stateSetterHandler(set);
  return {
    setOrder: handler('order', u => storage.order.set(u)),
    setGuestBookingInfo: handler('guestBookingInfo'),
    setStcPayData: handler('stcPayData'),
  };
});
